import { useCallback, useEffect, useState } from 'react';
import { allTimezones } from 'react-timezone-select';
import useCognito from '@/features/authentication/hooks/useCognito';
import useCurrentCloudUser from './getCurrentUser';
import useUpdateUser from './useUpdateUser';
import { getLeagueLogo } from '@/shared/services/getLeagueLogo';
import { League, Org, User, Department } from '@/types/user';
import { useUpdateSession } from './useUpdateSession';
import { useNavigate } from 'react-router-dom';
import { hasAccess } from '@/shared/services/hasAccess';
import { getLandingRoute } from '@/shared/services/getLandingRoute';

export type RoleCheckFunction = (roleNames: string | string[]) => boolean;

/**
 * Preference order for the league to open on when the user has no saved choice.
 * Values are `league.name` exactly as stored — note NHL is "HOCKEY_NHL", not
 * "NHL". FIFA_CLUB_WORLD_CUP is intentionally absent: it only comes up via the
 * trailing first-accessible fallback.
 */
const LEAGUE_PREFERENCE = ['HOCKEY_NHL', 'NBA', 'NFL', 'PREMIER_LEAGUE'];

/**
 * Resolve the league to start on, in LEAGUE_PREFERENCE order, falling back to
 * the first accessible league. Only reached when the API hands back no league —
 * an explicit choice always wins.
 */
const pickPreferredLeague = (leagues: League[]): League | null => {
  for (const name of LEAGUE_PREFERENCE) {
    const match = leagues.find(league => league.name?.toUpperCase() === name);
    if (match) return match;
  }
  return leagues[0] ?? null;
};

export interface UserState {
  userProfile: User | null;
  isLoading: boolean;
  error: Error | null;
  currentOrg: Org | null;
  currentLeague: League | null;
  availableLeagues: League[];
  availableOrgs: Org[];
  currentLeagueLogo: string | null;
  hasRole: RoleCheckFunction;
  userDepartments: Array<Department>;
  actions: {
    updateTimezone: (tz: string) => Promise<void>;
    updateName: (firstName: string, lastName: string) => Promise<void>;
    updateUseVenueTimezone: (useVenueTimezone: boolean) => Promise<void>;
    updateDefaultOrg: (org: Org) => Promise<void>;
    updateDefaultLeague: (league: Pick<League, 'id'>) => Promise<void>;
    setCurrentOrg: (org: Org) => void;
  };
}

const useUserState = (): UserState => {
  const navigate = useNavigate();
  const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Core mutations and queries
  const updateUserMutation = useUpdateUser();
  const updateSessionMutation = useUpdateSession();
  const { status, data: currentCloudUserQuery, refetch } = useCurrentCloudUser();


  const { authenticated } = useCognito();

  // State management
  const [error, setError] = useState<Error | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [currentOrg, setCurrentOrg] = useState<Org | null>(null);
  const [currentLeague, setCurrentLeague] = useState<League | null>(null);
  const [availableLeagues, setAvailableLeagues] = useState<League[]>([]);
  const [availableOrgs, setAvailableOrgs] = useState<Org[]>([]);
  const [currentLeagueLogo, setCurrentLeagueLogo] = useState<string | null>(null);
  const [userDepartments, setUserDepartments] = useState<Array<Department>>([]);

  // Image preloading utility
  const preloadImage = useCallback((url: string) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = resolve;
      img.onerror = reject;
      img.src = url;
    });
  }, []);

  // Initialize user data when cloud data is available
  useEffect(() => {
    if (status === 'success' && currentCloudUserQuery?.data) {
      const { org, league, leagues, user: cloudUser } = currentCloudUserQuery.data;

      if (!cloudUser || !org) {
        setError(new Error('Incomplete user data received'));
        return;
      }

      setCurrentOrg(org);
      // Pick a league when the payload carries none, rather than leaving the app
      // leagueless: GamesFormV2 hides the season picker while currentLeague is
      // null, and that picker is what first pushes filter values up to GamesPage,
      // so the games list sits empty until the user opens the menu and chooses a
      // league by hand.
      setCurrentLeague(league ?? pickPreferredLeague(leagues ?? []));
      setAvailableLeagues(leagues ?? []);
      setAvailableOrgs(cloudUser.orgs);
      const resolvedTimezone = allTimezones[cloudUser.tz] ? cloudUser.tz : browserTz;
      setUserProfile({
        ...cloudUser,
        tz: resolvedTimezone,
        useVenueTimezone: cloudUser.useVenueTimezone ?? true,
      });

      setUserDepartments(cloudUser.departments);
    }
  }, [status, currentCloudUserQuery, browserTz]);

  // Handle league logo updates
  useEffect(() => {
    const loadLeagueLogos = async () => {
      try {
        if (availableLeagues.length > 0) {
          await Promise.all(
            availableLeagues.map(league => preloadImage(getLeagueLogo(league)))
          );
        }

        if (currentLeague?.id) {
          await preloadImage(getLeagueLogo(currentLeague));
          setCurrentLeagueLogo(getLeagueLogo(currentLeague));
        }
      } catch (error) {
        console.error('Failed to load league logos:', error);
      }
    };

    loadLeagueLogos();
  }, [availableLeagues, currentLeague, preloadImage]);

  // Refresh user data when authentication changes
  useEffect(() => {
    if (authenticated) {
      refetch();
    }
  }, [authenticated, refetch]);

  // Action handlers
  const updateTimezone = async (tz: string) => {
    try {
      await updateUserMutation.mutateAsync({ tz });
      setUserProfile(prev => prev ? { ...prev, tz } : null);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  };

  const updateName = async (firstName: string, lastName: string) => {
    try {
      await updateUserMutation.mutateAsync({ firstName, lastName });
      setUserProfile(prev => prev ? { ...prev, firstName, lastName } : null);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  };

  const updateUseVenueTimezone = async (useVenueTimezone: boolean) => {
    try {
      await updateUserMutation.mutateAsync({ useVenueTimezone });
      setUserProfile(prev => prev ? { ...prev, useVenueTimezone } : null);
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  };

  const updateDefaultOrg = async (org: Org) => {
    try {
      await updateSessionMutation.mutateAsync({ org: org.id });
      // Wait for user data to reload so currentOrg has the new org's access data
      const { data } = await refetch();
      // Roles come from the user's group IN THE NEW ORG, and the landing page
      // follows them. They have to be read off the refetched payload:
      // userProfile is only updated by the effect above, so it still holds the
      // old org's roles at this point.
      navigate(getLandingRoute(data?.data?.user?.roles));
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  };

  const updateDefaultLeague = useCallback(async (league: Pick<League, 'id'>) => {
    try {
      await updateSessionMutation.mutateAsync({ league: league.id });
      const { data } = await refetch();
      navigate(getLandingRoute(data?.data?.user?.roles));
    } catch (error) {
      setError(error as Error);
      throw error;
    }
  }, [updateSessionMutation, refetch, navigate]);

  // Role checking utility
  const hasRole = useCallback((roleNames: string | string[]): boolean => {

    return hasAccess(userProfile?.roles || [], Array.isArray(roleNames) ? roleNames : [roleNames]);
  }, [userProfile?.roles]);

  return {
    userProfile,
    isLoading: status === 'pending',
    error,
    currentOrg,
    currentLeague,
    availableLeagues,
    availableOrgs,
    currentLeagueLogo,
    userDepartments,
    hasRole,
    actions: {
      updateTimezone,
      updateName,
      updateUseVenueTimezone,
      updateDefaultOrg,
      updateDefaultLeague,
      setCurrentOrg,
    },
  };
};

export default useUserState;
